import { useMemo } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  Buttonless,
  Flex,
  FlexGrow,
  Modal,
  PaddingInset,
  spacing,
  Stack,
  Text,
} from '@cash-design-system/react'
import { InvestingDestination } from '@cash-web/protos/squareup/cash/paychecks/common.pb'
import { SetPaycheckAllocationAmountBlocker } from '@cash-web/protos/squareup/franklin/blockers/set_paycheck_allocation_amount_blocker.pb'
import { GiantPercentageInput } from '@cash-web/shared-feature-common'
import {
  ModalActionHeader,
  ModalBackAction,
  ModalCloseAction,
  ModalPaneHeader,
  ModalPortal,
  useModalActionsOverride,
} from '@cash-web/shared-feature-modal'
import { PaychecksDistributionHero } from '@cash-web/shared-feature-paychecks'
import { PieChart } from '@cash-web/shared-feature-pie-chart'
import { useJourneyContext, useJourneyScope } from '@cash-web/shared-journey-data-access-context'
import { replaceWithCDNImageUrl } from '@cash-web/shared-util-url'

import { BaseBlocker, BlockerProps } from '..'
import { useQueryGetAllInvestmentEntities } from '../BlockerInstrumentSelection/useQueryGetAllInvestmentEntities'
import { useBlockerSetAllocationAmount } from './useBlockerSetAllocationAmount'

const PRESET_ALLOCATIONS = [100, 500, 1000, 1500, 2000]

const AllocationButtonsWrapper = styled(Flex)`
  margin-bottom: ${({ theme: { spacing } }) => spacing.m};
`

const AllocationButton = styled(Button)`
  flex-basis: 30%;
`

const ModalStack = styled(Stack)`
  padding-top: ${({ theme: { spacing } }) => spacing.xxl};
`
interface Props extends BlockerProps {
  blocker: SetPaycheckAllocationAmountBlocker
}

const SmallIcon = styled.img`
  object-fit: contain;
  width: 1.75rem;
  height: 1.75rem;
`

type InvestmentSelectionProps = {
  investingDestination: InvestingDestination
  onEdit: () => void
}
const InvestmentSelection = ({ investingDestination, onEdit }: InvestmentSelectionProps) => {
  const { t } = useTranslation()
  const { colors, spacing } = useTheme()
  useQueryGetAllInvestmentEntities() // preloading this large amount of data here for later

  const { ui_specification } = investingDestination
  if (!ui_specification) return null
  const { display_name, icon, symbol } = ui_specification

  return (
    <Buttonless onClick={onEdit} type="button">
      <Box
        border={`0.125rem solid ${colors.secondaryButtonBackground}`}
        borderRadius="3rem / 3rem"
        padding={`${spacing.xs} ${spacing.m}`}
      >
        <Flex gap={spacing.xs} alignItems="center">
          <SmallIcon src={replaceWithCDNImageUrl({ url: icon?.light_url, width: 1.75 * 16 })} alt={display_name} />
          <FlexGrow>
            <Text typography="button" color="textStandard">
              {display_name}&nbsp;
            </Text>
            <Text typography="input" color="textSubtle">
              ({symbol})
            </Text>
          </FlexGrow>
          <Box padding="0 s">
            <Text typography="button" color="textStandard">
              {t('Edit', { context: 'button label' })}
            </Text>
          </Box>
        </Flex>
      </Box>
    </Buttonless>
  )
}

export function BlockerSetAllocationAmount({ blocker }: Props): React.ReactElement {
  const { t } = useTranslation()
  const journey = useJourneyContext()
  const journeyScope = useJourneyScope()
  const {
    form,
    allocationBasis,
    closeCustomAmountModal,
    customAmount,
    investingDestination,
    setCustomAmount,
    shouldSubmitBeDisabled,
    setShouldShowManualInput,
    shouldShowManualInput,
    handleDisableButtonClick,
    handleBasisChange,
    onEditInvestmentEntity,
    onSubmit,
    onBack,
    estimatedCurrencyAmountText,
    uiElements,
    totalAvailableAllocation,
    arcs,
    hero,
    shouldShowExceededMaxDistributionAlert,
    handleExceededMaxDistributionAlertAcknowledge,
    selectedDestination,
  } = useBlockerSetAllocationAmount(blocker)

  useModalActionsOverride(
    useMemo(
      () =>
        journeyScope.target === 'fullscreen' ? (
          <ModalPaneHeader>
            <ModalBackAction onBack={onBack} />
          </ModalPaneHeader>
        ) : (
          <ModalActionHeader>
            <ModalCloseAction
              onClose={() => {
                journey.endAll()
              }}
            />
            <ModalBackAction onBack={onBack} />
          </ModalActionHeader>
        ),
      [journey, onBack, journeyScope.target]
    )
  )

  if (shouldShowExceededMaxDistributionAlert) {
    return (
      <Modal onClose={handleExceededMaxDistributionAlertAcknowledge} showClose={false}>
        <ModalStack space={spacing.m}>
          <Text typography="labelDefault">
            {blocker.exceeded_max_distribution_alert_ui?.title_text?.translated_value}
          </Text>
          <div>{blocker.exceeded_max_distribution_alert_ui?.detail_text?.translated_value}</div>
          <Button size="large" onClick={handleExceededMaxDistributionAlertAcknowledge}>
            {blocker.exceeded_max_distribution_alert_ui?.acknowledge_text?.translated_value}
          </Button>
        </ModalStack>
      </Modal>
    )
  }

  const allocationOptions = uiElements.atm_picker_options_in_basis_points ?? PRESET_ALLOCATIONS

  const manualInputTitle =
    selectedDestination === 'investing' || selectedDestination === 'bitcoin'
      ? t('How much do you want to invest?', { context: 'distribution input label' })
      : t('How much do you want to save?', { context: 'distribution input label' })

  return (
    <BaseBlocker mainText={uiElements.header?.translated_value} form={form} onSubmit={onSubmit} buttons={[]}>
      <PieChart
        arcs={arcs}
        hero={<PaychecksDistributionHero hero={hero} avatar_url={hero.avatar_url} iconUnicode={hero.iconUnicode} />}
      />
      <PaddingInset>{estimatedCurrencyAmountText && <Text>{estimatedCurrencyAmountText}</Text>}</PaddingInset>
      <AllocationButtonsWrapper flexWrap="wrap" gap={spacing.m} justifyContent="space-between">
        {allocationOptions.map(amount => (
          <AllocationButton key={amount} variant="secondary" onClick={() => handleBasisChange(amount)}>
            {`${amount / 100}%`}
          </AllocationButton>
        ))}
        <AllocationButton variant="secondary" onClick={() => setShouldShowManualInput(true)}>
          {'. . .'}
        </AllocationButton>
      </AllocationButtonsWrapper>
      {shouldShowManualInput && (
        <ModalPortal onClose={closeCustomAmountModal}>
          <Stack space={spacing.l}>
            <Text typography="numeralS" color="textStandard">
              {manualInputTitle}
            </Text>
            <GiantPercentageInput
              name="distributionAmount"
              setFormattedValue={setCustomAmount}
              minimumLimit={0}
              maximumLimit={totalAvailableAllocation / 100}
              formattedValue={customAmount}
              value={Number(allocationBasis)}
              isAllowed={({ floatValue }) => (floatValue ?? 0) <= 100}
            />
            <Button size="large" onClick={closeCustomAmountModal}>
              {t('Next', { context: 'button text' })}
            </Button>
          </Stack>
        </ModalPortal>
      )}
      {investingDestination && (
        <InvestmentSelection investingDestination={investingDestination} onEdit={onEditInvestmentEntity} />
      )}
      {(uiElements.secondary_button || uiElements.primary_button) && (
        <Flex justifyContent="space-between" gap={spacing.l}>
          {uiElements.secondary_button && (
            <Button size="large" variant="secondary" onClick={handleDisableButtonClick}>
              {uiElements.secondary_button.label?.translated_value}
            </Button>
          )}
          {uiElements.primary_button && (
            <Button
              type="submit"
              loading={form.formState.isSubmitting}
              disabled={shouldSubmitBeDisabled}
              onClick={onSubmit}
            >
              {uiElements.primary_button.label?.translated_value}
            </Button>
          )}
        </Flex>
      )}
    </BaseBlocker>
  )
}
